// 'use client';

// import Link from 'next/link';
// import { Button } from '@/components/ui/button';
// import { RudriseLogo } from '@/components/rudrise-logo';
// import { useState } from 'react';

// export function LandingHeader() {
//   const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

//   const navLinks = [
//     { href: '/', label: 'Home' },
//     { href: '/pricing', label: 'Pricing' },
//     { href: '/download', label: 'Download' },
//     { href: '/help', label: 'Help' },
//     { href: '/about', label: 'About Us' },
//   ];

//   return (
//     <header className="border-b border-border bg-background sticky top-0 z-50">
//       <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
//         <div className="flex items-center justify-between h-16">
//           {/* Logo — uses the main project's shared <RudriseLogo /> component */}
//           <Link href="/" className="flex items-center gap-2">
//             <RudriseLogo size="sm" />
//           </Link>

//           {/* Desktop Navigation */}
//           <nav className="hidden md:flex items-center gap-1">
//             {navLinks.map((link) => (
//               <Link
//                 key={link.href}
//                 href={link.href}
//                 className="px-3 py-2 text-sm font-medium text-foreground hover:bg-secondary rounded-md transition-colors"
//               >
//                 {link.label}
//               </Link>
//             ))}
//             <div className="h-6 w-px bg-border mx-2" />
//             <Link
//               href="/editor"
//               className="px-3 py-2 text-sm font-medium text-primary hover:bg-secondary rounded-md transition-colors flex items-center gap-1"
//             >
//               Try LaTeX Editor
//               <span className="text-xs">↗</span>
//             </Link>
//           </nav>

//           {/* Auth Buttons */}
//           <div className="hidden sm:flex items-center gap-3">
//             <Link href="/sign-in">
//               <Button variant="outline" size="sm">
//                 Sign In
//               </Button>
//             </Link>
//             <Link href="/sign-up">
//               <Button size="sm">Sign Up</Button>
//             </Link>
//           </div>

//           {/* Mobile Menu Button */}
//           <button
//             className="md:hidden p-2 hover:bg-secondary rounded-md"
//             onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
//           >
//             <svg
//               className="w-6 h-6"
//               fill="none"
//               stroke="currentColor"
//               viewBox="0 0 24 24"
//             >
//               <path
//                 strokeLinecap="round"
//                 strokeLinejoin="round"
//                 strokeWidth={2}
//                 d="M4 6h16M4 12h16M4 18h16"
//               />
//             </svg>
//           </button>
//         </div>

//         {/* Mobile Navigation */}
//         {mobileMenuOpen && (
//           <div className="md:hidden border-t border-border py-4 space-y-2">
//             {navLinks.map((link) => (
//               <Link
//                 key={link.href}
//                 href={link.href}
//                 className="block px-3 py-2 text-sm font-medium text-foreground hover:bg-secondary rounded-md transition-colors"
//                 onClick={() => setMobileMenuOpen(false)}
//               >
//                 {link.label}
//               </Link>
//             ))}
//             <div className="pt-4 border-t border-border space-y-2">
//               <Link href="/sign-in" className="block">
//                 <Button variant="outline" size="sm" className="w-full">
//                   Sign In
//                 </Button>
//               </Link>
//               <Link href="/sign-up" className="block">
//                 <Button size="sm" className="w-full">
//                   Sign Up
//                 </Button>
//               </Link>
//             </div>
//           </div>
//         )}
//       </div>
//     </header>
//   );
// }

"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { RudriseLogo } from "@/components/rudrise-logo";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth-context";
import { hasAuthSession } from "@/lib/auth-client";
import { useThemeMode } from "@/lib/themes/theme-mode";
import { dashboardThemeVars } from "@/lib/themes/dashboard-theme-vars";
import { performFullLogout } from "@/lib/auth-cleanup";
import { LogoutConfirmDialog } from "@/components/dashboard/logout-confirm-dialog";

export function LandingHeader() {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const [loggingOut, setLoggingOut] = useState(false);

    const pathname = usePathname();
    const router = useRouter();
    const { isAuthenticated, isInitialized, logout } = useAuth();
    const { theme } = useThemeMode();

    useEffect(() => {
        document.documentElement.classList.toggle("dark", theme.name === "dark" || theme.name === "gray");
    }, [theme.name]);

    const showAuthenticatedUI =
        isAuthenticated || (!isInitialized && hasAuthSession());

    const authReady = isInitialized || hasAuthSession();

    const handleLogout = async () => {
        setLoggingOut(true);
        try {
            await logout().catch(() => {});
        } finally {
            await performFullLogout("/");
        }
    };

    const navLinks = [
        { href: "/", label: "Home" },
        { href: "/pricing", label: "Pricing" },
        { href: "/download", label: "Download" },
        { href: "/help", label: "Help" },
        { href: "/about", label: "About Us" },
        { href: "/store", label: "Store" },
    ];

    return (
        <>
            <header
                className="sticky top-0 z-50 bg-[var(--db-content-bg)]/80 backdrop-blur-md border-b border-[var(--db-border)]"
                style={dashboardThemeVars(theme)}
            >
                <div className="w-full px-6 h-16 relative flex items-center justify-between">
                    {/* LEFT: Logo */}
                    <Link href="/" className="flex items-center gap-2">
                        <RudriseLogo size="sm" />
                    </Link>

                    {/* CENTER: Pill Navigation */}
                    <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 hidden min-[1050px]:flex items-center">
                        <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-[var(--db-canvas-bg)] shadow-inner border border-[var(--db-border)]">
                            {navLinks.map((link) => {
                                const isActive = pathname === link.href;

                                return (
                                    <Link
                                        key={link.href}
                                        href={link.href}
                                        className={cn(
                                            "px-[clamp(8px,1.5vw,16px)] py-1.5 text-[clamp(11px,1vw,14px)] whitespace-nowrap rounded-full transition-all",
                                            isActive
                                                ? "text-[var(--db-user-header)] font-medium"
                                                : "text-[var(--db-text-secondary,#6b7280)] hover:text-[var(--db-text-primary)]"
                                        )}
                                    >
                                        {link.label}
                                    </Link>
                                );
                            })}

                            {!showAuthenticatedUI ? (
                                <>
                                    <div className="mx-2 h-4 w-px bg-[var(--db-border)]" />
                                    <Link
                                        href="/editor?loadTemplate=1"
                                        className="px-[clamp(8px,1.5vw,16px)] py-1.5 text-[clamp(11px,1vw,14px)] whitespace-nowrap rounded-full text-[var(--db-user-header)] font-medium hover:bg-[var(--db-content-bg)] transition-all flex items-center gap-1"
                                    >
                                        Try LaTeX Editor
                                        <span className="text-xs">↗</span>
                                    </Link>
                                </>
                            ) : null}
                        </div>
                    </div>

                    {/* RIGHT: Auth */}
                    <div className="hidden sm:flex items-center gap-3">
                        {!authReady ? (
                            <div className="h-9 w-[220px] rounded-full bg-[var(--db-canvas-bg)] animate-pulse" aria-hidden />
                        ) : showAuthenticatedUI ? (
                            <>
                                <Link href="/dashboard">
                                    <Button
                                        size="sm"
                                        className="rounded-full px-5 text-[var(--db-button-text)] hover:opacity-90"
                                        style={{ backgroundColor: "var(--db-user-header)" }}
                                    >
                                        Go to Dashboard
                                    </Button>
                                </Link>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="rounded-full px-5 border-[var(--db-border)] text-[var(--db-text-primary)] hover:bg-[var(--db-canvas-bg)]"
                                    onClick={() => setShowLogoutConfirm(true)}
                                >
                                    Logout
                                </Button>
                            </>
                        ) : (
                            <>
                                <Link href="/sign-in">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="rounded-full px-5 border-[var(--db-border)] text-[var(--db-text-primary)] hover:bg-[var(--db-canvas-bg)]"
                                    >
                                        Sign In
                                    </Button>
                                </Link>
                                <Link href="/sign-up">
                                    <Button
                                        size="sm"
                                        className="rounded-full px-5 text-[var(--db-button-text)] hover:opacity-90"
                                        style={{ backgroundColor: "var(--db-user-header)" }}
                                    >
                                        Sign Up
                                    </Button>
                                </Link>
                            </>
                        )}
                    </div>

                    {/* MOBILE MENU BUTTON */}
                    <button
                        className="min-[1050px]:hidden p-2 rounded-md hover:bg-[var(--db-canvas-bg)] text-[var(--db-text-primary)]"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    >
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 6h16M4 12h16M4 18h16"
                            />
                        </svg>
                    </button>
                </div>

                {/* MOBILE MENU */}
                {mobileMenuOpen && (
                    <div className="min-[1050px]:hidden border-t border-[var(--db-border)] px-4 py-4 space-y-2 bg-[var(--db-content-bg)]">
                        {navLinks.map((link) => {
                            const isActive = pathname === link.href;

                            return (
                                <Link
                                    key={link.href}
                                    href={link.href}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={cn(
                                        "block px-3 py-2 rounded-md text-sm",
                                        isActive
                                            ? "bg-[var(--db-card-selected-bg)] text-[var(--db-user-header)] font-medium"
                                            : "text-[var(--db-text-secondary,#6b7280)] hover:bg-[var(--db-canvas-bg)] hover:text-[var(--db-text-primary)]"
                                    )}
                                >
                                    {link.label}
                                </Link>
                            );
                        })}

                        <div className="sm:hidden pt-4 border-t border-[var(--db-border)] space-y-2">
                            {!authReady ? (
                                <div className="h-9 w-full rounded-full bg-[var(--db-canvas-bg)] animate-pulse" aria-hidden />
                            ) : showAuthenticatedUI ? (
                                <>
                                    <Link href="/dashboard" className="block" onClick={() => setMobileMenuOpen(false)}>
                                        <Button
                                            size="sm"
                                            className="w-full rounded-full text-[var(--db-button-text)] hover:opacity-90"
                                            style={{ backgroundColor: "var(--db-user-header)" }}
                                        >
                                            Go to Dashboard
                                        </Button>
                                    </Link>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="w-full rounded-full border-[var(--db-border)] text-[var(--db-text-primary)] hover:bg-[var(--db-canvas-bg)]"
                                        onClick={() => {
                                            setMobileMenuOpen(false);
                                            setShowLogoutConfirm(true);
                                        }}
                                    >
                                        Logout
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Link href="/sign-in" className="block" onClick={() => setMobileMenuOpen(false)}>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="w-full rounded-full border-[var(--db-border)] text-[var(--db-text-primary)] hover:bg-[var(--db-canvas-bg)]"
                                        >
                                            Sign In
                                        </Button>
                                    </Link>
                                    <Link href="/sign-up" className="block" onClick={() => setMobileMenuOpen(false)}>
                                        <Button
                                            size="sm"
                                            className="w-full rounded-full text-[var(--db-button-text)] hover:opacity-90"
                                            style={{ backgroundColor: "var(--db-user-header)" }}
                                        >
                                            Sign Up
                                        </Button>
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </header>

            {showLogoutConfirm && (
                <LogoutConfirmDialog
                    loading={loggingOut}
                    onCancel={() => setShowLogoutConfirm(false)}
                    onConfirm={handleLogout}
                />
            )}
        </>
    );
}